import EnrollmentForm from "@/components/enrollment/EnrollmentForm";
import RefundForm from "@/components/enrollment/RefundForm";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { formatCurrency } from "@/lib/utils/currency";
import {
    EnrollmentTier,
    getEnrollmentTierLabel,
    RequestHelpType,
} from "@/lib/utils/enrollment";
import { getColumnValues } from "@/lib/utils/table";
import { Status } from "@/types/enums";
import { FieldWithEnrollment, ImageryExtent } from "@/types/field";
import {
    faDownload,
    faArrowUpFromBracket,
    faSearch,
    faArrowUpShortWide,
    faArrowDownShortWide,
    faPencil,
    faArrowRotateLeft,
    faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardHeader,
    Checkbox,
    Chip,
    Divider,
    Input,
    Spinner,
    Switch,
} from "@heroui/react";
import { createFileRoute } from "@tanstack/react-router";
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Header,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { useMemo, useState, useCallback, useRef } from "react";
import sentinelLogo from "@/assets/images/Sentinel_Horizontal_TM.png"

export const Route = createFileRoute("/dashboard/enrollment/fields")({
    component: RouteComponent,
});

function RouteComponent() {
    const { useGetRequest, useMutateRequest } = useRequest();

    const enrollmentQuery = useGetRequest<FieldWithEnrollment[]>({
        url: "fields/enrollment_status/",
        queryOptions: {
            refetchOnWindowFocus: false,
        },
    });
    const remainingBalance = useGetRequest<{
        remaining_retail_value: number;
    }>({
        url: "field_enrollments/remaining_retail_value/",
    });
    const { userIsSentinel, isConsultant } = useAuth();

    const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
    const [showEnrollmentForm, setShowEnrollmentForm] =
        useState<boolean>(false);
    const [showRefundForm, setShowRefundForm] = useState<boolean>(false);

    const patchField = useMutateRequest({
        method: "patch",

        errorMessage: "Failed to update field",
        successMessage: "Successfully updated field",
    });

    const columns: ColumnDef<FieldWithEnrollment>[] = useMemo(() => {
        const tableColumns: ColumnDef<FieldWithEnrollment>[] = [
            {
                id: "select",
                header: "",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.id;
                },
                size: 50,
                cell: ({ row }) => {
                    return (
                        <Checkbox
                            size="sm"
                            isDisabled={!!row.original.active_enrollment}
                            isReadOnly={!!row.original.active_enrollment}
                            isSelected={selectedRowIds.includes(
                                row.original.id,
                            )}
                            onValueChange={(value) => {
                                if (value) {
                                    setSelectedRowIds([
                                        ...selectedRowIds,
                                        row.original.id,
                                    ]);
                                } else {
                                    setSelectedRowIds(
                                        selectedRowIds.filter(
                                            (r) => r !== row.original.id,
                                        ),
                                    );
                                }
                            }}
                        />
                    );
                },
            },
            {
                header: "Active",
                id: "status",
                accessorKey: "status",
                cell: ({ row }) => {
                    return (
                        <Switch
                            size="sm"
                            isSelected={row.original.status == 1}
                            onValueChange={(isSelected) => {
                                patchField.mutate(
                                    {
                                        url: `fields/${row.original.id}/`,
                                        data: {
                                            status: isSelected ? 1 : 2,
                                        },
                                    },
                                    {
                                        onSuccess: () => {
                                            enrollmentQuery.refetch();
                                        },
                                    },
                                );
                            }}
                        />
                    );
                },
                size: 80,
            },
            {
                id: "csp",
                header: "CSP",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.grower.service_provider?.name;
                },
                size: 200,
            },
            {
                id: "farm",
                header: "Farm",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.grower.name;
                },
                size: 200,
            },
            {
                id: "field",
                header: "Field",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.name;
                },
            },
            {
                id: "enrollmentStatus",
                header: "Enrollment Status",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return (
                            <Chip size="sm" color="danger">
                                Inactive
                            </Chip>
                        );
                    }
                    if (row.original.active_enrollment.is_scheduled) {
                        return (
                            <Chip
                                size="sm"
                                classNames={{
                                    base: "bg-blue-500 text-white",
                                }}
                            >
                                Scheduled
                            </Chip>
                        );
                    }
                    if (row.original.active_enrollment.is_active) {
                        return (
                            <Chip
                                size="sm"
                                color="secondary"
                                className="text-white"
                            >
                                Enrolled
                                {row.original.active_enrollment.refunded_on
                                    ? " (Refunded)"
                                    : ""}
                            </Chip>
                        );
                    }
                },
            },
            {
                id: "startDate",
                header: "Start Date",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.start_date;
                },
                cell: ({ row }) => {
                    return (
                        <div>
                            {dayjs(
                                row.original.active_enrollment?.start_date,
                            ).format("MM/DD/YYYY")}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "insightsStartDate",
                header: "Insights Start",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.insights_start_date;
                },
                cell: ({ row }) => {
                    return (
                        <div>
                            {dayjs(
                                row.original.active_enrollment
                                    ?.insights_start_date,
                            ).format("MM/DD/YYYY")}
                        </div>
                    );
                },
                size: 120,
            },
            {
                id: "acres",
                header: "Acres",
                accessorFn: (row: FieldWithEnrollment) => {
                    return (
                        row.active_enrollment?.acres ??
                        row.imagery_boundary_acres
                    );
                },
                size: 100,
            },
            {
                id: "boundaryType",
                header: "Boundary Type",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.imagery_extent;
                },
                cell: ({ row }) => {
                    return (
                        <ButtonGroup
                            size="sm"
                            isDisabled={!!row.original.active_enrollment}
                        >
                            <Button
                                color={
                                    row.original.imagery_extent ==
                                        ImageryExtent.FIELD
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            url: `fields/${row.original.id}/`,
                                            data: {
                                                imagery_extent:
                                                    ImageryExtent.FIELD,
                                            },
                                        },
                                        {
                                            onSuccess: () => {
                                                enrollmentQuery.refetch();
                                            },
                                        },
                                    );
                                }}
                            >
                                Field
                            </Button>
                            <Button
                                color={
                                    row.original.imagery_extent ==
                                        ImageryExtent.FIELD
                                        ? "default"
                                        : "primary"
                                }
                                isDisabled={!row.original.has_irrigation_system}
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            url: `fields/${row.original.id}/`,
                                            data: {
                                                imagery_extent:
                                                    ImageryExtent.IRRIGATED_REGION,
                                            },
                                        },
                                        {
                                            onSuccess: () => {
                                                enrollmentQuery.refetch();
                                            },
                                        },
                                    );
                                }}
                            >
                                Irrigated
                            </Button>
                        </ButtonGroup>
                    );
                },
            },
            {
                id: "tier",
                header: "Tier",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.enrollment_tier;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return <div></div>;
                    }
                    return (
                        <div>
                            {getEnrollmentTierLabel(
                                row.original.active_enrollment?.enrollment_tier,
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "price",
                header: "Price",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.retail_price_per_acre;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return <div></div>;
                    }
                    return (
                        <div>
                            {formatCurrency(
                                row.original.active_enrollment
                                    ?.retail_price_per_acre,
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "total",
                header: "Total",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.credits;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return <div></div>;
                    }
                    return (
                        <div>
                            {formatCurrency(
                                row.original.active_enrollment?.credits,
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "actions",
                header: "Actions",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.id;
                },
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-row gap-1 w-full">
                            {!row.original.active_enrollment && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    fullWidth
                                    onPress={() => {
                                        setSelectedRowIds([row.original.id]);
                                        setShowEnrollmentForm(true);
                                    }}
                                    startContent={
                                        <FontAwesomeIcon icon={faPlus} />
                                    }
                                >
                                    Enroll
                                </Button>
                            )}
                            {row.original.active_enrollment && (
                                <Button
                                    color="primary"
                                    size="sm"
                                    fullWidth
                                    onPress={() => {
                                        setSelectedRowIds([row.original.id]);
                                        setShowEnrollmentForm(true);
                                    }}
                                    startContent={
                                        <FontAwesomeIcon icon={faPencil} />
                                    }
                                >
                                    Edit
                                </Button>
                            )}
                            {row.original.active_enrollment &&
                                !row.original.active_enrollment.refunded_on &&
                                userIsSentinel && (
                                    <Button
                                        color="primary"
                                        size="sm"
                                        fullWidth
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faArrowRotateLeft}
                                            />
                                        }
                                        onPress={() => {
                                            setSelectedRowIds([
                                                row.original.id,
                                            ]);
                                            setShowRefundForm(true);
                                        }}
                                    >
                                        Refund
                                    </Button>
                                )}
                        </div>
                    );
                },
            },
        ];

        if (!userIsSentinel) {
            tableColumns.splice(2, 1);
        }

        return tableColumns;
    }, [enrollmentQuery, patchField, selectedRowIds, userIsSentinel]);

    const fieldsToEnroll = useMemo(() => {
        return (
            enrollmentQuery?.data?.filter((row) =>
                selectedRowIds.includes(row.id),
            ) ?? []
        );
    }, [enrollmentQuery, selectedRowIds]);

    const [globalFilter, setGlobalFilter] = useState("");
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const table = useReactTable({
        columns,
        data: enrollmentQuery?.data ?? [],
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            globalFilter,
            columnFilters,
        },
        initialState: {
            sorting: [
                {
                    id: "field",
                    desc: true,
                },
            ],
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
        onColumnFiltersChange: setColumnFilters,
    });

    const { rows: filteredRows } = table.getFilteredRowModel();
    const { rows } = table.getRowModel();

    const downloadPDF = useCallback(() => {
        const filteredData = filteredRows;
        const columns = [
            "Status",
            "Farm",
            "Field",
            "Enrollment Status",
            "Imagery Start Date",
            "Insights Start Date",
            "Acres",
            "Boundary Type",
            "Tier",
            "Price",
            "Total",
        ];
        const isSentinel = userIsSentinel;
        if (isSentinel) {
            columns.splice(1, 0, "CSP");
        }
        const rows = filteredData.map((rowField) => {
            const field = rowField.original;
            const row = [
                field.status === Status.Active ? "Active" : "Inactive",
                field.grower.name,
                field.name,
                field.active_enrollment
                    ? field.active_enrollment.refunded_on
                        ? "Enrolled (Refunded)"
                        : field.active_enrollment.is_scheduled
                            ? "Scheduled"
                            : "Enrolled"
                    : "Inactive",
                field.active_enrollment?.start_date,
                field.active_enrollment?.insights_start_date,
                field.active_enrollment?.acres ?? field.imagery_boundary_acres,
                field.imagery_extent == ImageryExtent.FIELD
                    ? "Field"
                    : "Irrigated",
                getEnrollmentTierLabel(
                    field.active_enrollment?.enrollment_tier,
                ),
                field.active_enrollment?.retail_price_per_acre,
                field.active_enrollment?.credits,
            ];
            if (isSentinel) {
                row.splice(1, 0, field.grower.service_provider?.name);
            }
            return row as (string | number)[];
        });

        const doc = new jsPDF();
        const image = new Image();
        image.src = sentinelLogo;
        image.onload = () => {
            doc.addImage(image, "PNG", 15, 10, 80, 20);
            const today = dayjs().format("MM/DD/YYYY");
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.setFontSize(12);
            doc.text(`${today}`, pageWidth - 40, 20);
            const textWidth = doc.getTextWidth("Enrollment Summary");
            const x = (pageWidth - textWidth) / 2;
            doc.setFontSize(16);
            doc.text("Enrollment Summary", x, 35);
            doc.setFontSize(6);
            autoTable(doc, {
                columns: columns,
                body: rows,
                startY: 40,
                theme: "grid",
                bodyStyles: {
                    fontSize: 8,
                    overflow: "linebreak",
                },
                headStyles: {
                    fontSize: 8,
                    fillColor: [0, 51, 102],
                    textColor: [255, 255, 255],
                    overflow: "linebreak",
                },
            });
            doc.save("Sentinel Report.pdf");
        };
    }, [filteredRows, userIsSentinel]);

    const { rows: prefilteredRows } = table.getPreFilteredRowModel();

    const filterOptions = useMemo(() => {
        const filters = {
            farm: getColumnValues("farm", prefilteredRows).map((farm) => {
                return (
                    <AutocompleteItem key={farm} textValue={farm ?? "N/A"}>
                        {farm}
                    </AutocompleteItem>
                );
            }),
            csp: undefined,
        };
        if (userIsSentinel) {
            return {
                ...filters,
                csp: getColumnValues("csp", prefilteredRows)
                    .filter((csp) => !!csp)
                    .map((csp) => {
                        return (
                            <AutocompleteItem
                                key={csp}
                                textValue={csp ?? "N/A"}
                            >
                                {csp}
                            </AutocompleteItem>
                        );
                    }),
            };
        }
        return filters;
    }, [userIsSentinel, prefilteredRows]);

    const ref = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => 33,
        getScrollElement: () => ref.current,
        measureElement:
            typeof window !== "undefined" &&
                navigator.userAgent.indexOf("Firefox") === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
        overscan: 5,
    });

    const headersById = useMemo(() => {
        return table
            .getHeaderGroups()
            .map((group) => group.headers.map((header) => header))
            .flat()
            .reduce(
                (acc, header) => {
                    acc[header.id] = header;
                    return acc;
                },
                {} as Record<string, Header<FieldWithEnrollment, unknown>>,
            );
    }, [table]);

    const summary = useMemo(() => {
        return (
            filteredRows?.reduce(
                (acc, row) => {
                    const fe = row.original;
                    if (fe.active_enrollment) {
                        if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Basic
                        ) {
                            acc.basicAcres += fe.active_enrollment.acres;
                            acc.basicValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Advanced
                        ) {
                            acc.advancedAcres += fe.active_enrollment.acres;
                            acc.advancedValue += fe.active_enrollment.credits;
                        }
                        acc.enrolledAcres += fe.active_enrollment.acres;
                        acc.enrolledValue += fe.active_enrollment.credits;
                    } else {
                        if (fe.status == Status.Active) {
                            acc.activeUnenrolledAcres +=
                                fe.imagery_boundary_acres;
                        } else {
                            acc.inactiveUnenrolledAcres +=
                                fe.imagery_boundary_acres;
                        }
                    }
                    return acc;
                },
                {
                    basicAcres: 0,
                    advancedAcres: 0,
                    enrolledAcres: 0,
                    basicValue: 0,
                    advancedValue: 0,
                    enrolledValue: 0,
                    activeUnenrolledAcres: 0,
                    inactiveUnenrolledAcres: 0,
                },
            ) ?? {}
        );
    }, [filteredRows]);

    const requestHelp = useMutateRequest({
        url: `field_enrollments/request_help/`,
        method: "post",
        errorMessage: "Failed to request help",
        successMessage: "Your request has been sent to your account manager",
    });

    const requestAdditionalBalance = useCallback(
        () =>
            requestHelp.mutate({
                data: {
                    type: RequestHelpType.request_balance_update,
                },
            }),
        [requestHelp],
    );

    return (
        <div className="p-2 flex flex-col w-full h-full">
            <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row gap-2 items-center">
                    <Input
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        value={globalFilter}
                        size="sm"
                        isClearable
                        onClear={() => setGlobalFilter("")}
                        onChange={(e) =>
                            setGlobalFilter(String(e.target.value))
                        }
                        placeholder={"Search.."}
                    />
                    {userIsSentinel && (
                        <Autocomplete
                            color={
                                headersById["csp"].column.getFilterValue()
                                    ? "primary"
                                    : "default"
                            }
                            aria-label="Filter"
                            className="w-full pointer-events-auto"
                            size="sm"
                            placeholder="CSP"
                            showScrollIndicators={false}
                            selectedKey={
                                headersById[
                                    "csp"
                                ].column.getFilterValue() as string
                            }
                            onSelectionChange={(e) => {
                                headersById["csp"].column.setFilterValue(e);
                            }}
                        >
                            {filterOptions?.csp ?? (
                                <AutocompleteItem>N/A</AutocompleteItem>
                            )}
                        </Autocomplete>
                    )}
                    <Autocomplete
                        color={
                            headersById["farm"].column.getFilterValue()
                                ? "primary"
                                : "default"
                        }
                        aria-label="Filter"
                        className="w-full pointer-events-auto"
                        size="sm"
                        placeholder="Farm"
                        showScrollIndicators={false}
                        selectedKey={
                            headersById[
                                "farm"
                            ].column.getFilterValue() as string
                        }
                        onSelectionChange={(e) => {
                            headersById["farm"].column.setFilterValue(e);
                        }}
                    >
                        {filterOptions?.farm}
                    </Autocomplete>
                    {isConsultant && (
                        <div className="text-sm flex items-center gap-2 min-w-[500px]">
                            <span>Remaining Retail Balance:</span>
                            <Chip color="primary" radius="sm">
                                {formatCurrency(
                                    remainingBalance?.data
                                        ?.remaining_retail_value,
                                )}
                            </Chip>
                            <Button
                                size="sm"
                                color="primary"
                                onPress={requestAdditionalBalance}
                                isLoading={requestHelp.isPending}
                            >
                                Request Additional Balance
                            </Button>
                        </div>
                    )}
                </div>
                <div className="flex flex-row gap-2 items-center">
                    <Button
                        color="primary"
                        onPress={downloadPDF}
                        size="sm"
                        startContent={<FontAwesomeIcon icon={faDownload} />}
                    >
                        Download PDF
                    </Button>
                    <Button
                        color="secondary"
                        onPress={() => {
                            setShowEnrollmentForm(true);
                        }}
                        size="sm"
                        isDisabled={selectedRowIds.length === 0}
                        startContent={
                            <FontAwesomeIcon icon={faArrowUpFromBracket} />
                        }
                    >
                        Enroll Selected Fields
                    </Button>
                </div>
            </div>
            <div
                ref={ref}
                className="overflow-y-scroll text-xs bg-white w-full h-[600px]"
            >
                <table style={{ height: 600, width: "100%" }}>
                    <thead
                        style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                        className="bg-white w-full"
                    >
                        {table.getHeaderGroups().map((group) => (
                            <tr className="border-b py-[0.25rem] border-gray-700 w-full flex flex-row items-center justify-items-center">
                                {group.headers.map((header) => {
                                    return (
                                        <th
                                            aria-label={
                                                header.column.columnDef.header?.toString() ??
                                                "column header"
                                            }
                                            key={header.id}
                                            style={{
                                                width: header.column.getSize(),
                                            }}
                                            className="pointer-events-none"
                                        >
                                            <div
                                                onClick={header.column.getToggleSortingHandler()}
                                                className={classNames({
                                                    "hover:bg-gray-200 rounded-lg p-1 text-sm pointer-events-auto w-full flex text-start":
                                                        true,
                                                    "cursor-pointer":
                                                        header.column.getCanSort(),
                                                })}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {header.column.getCanSort() &&
                                                    header.column.getIsSorted() && (
                                                        <FontAwesomeIcon
                                                            className="ml-2 text-primary-500"
                                                            icon={
                                                                header.column.getIsSorted() ===
                                                                    "asc"
                                                                    ? faArrowUpShortWide
                                                                    : faArrowDownShortWide
                                                            }
                                                            size="sm"
                                                        />
                                                    )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            position: "relative",
                            width: table.getCenterTotalSize(),
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[
                                virtualRow.index
                            ] as Row<FieldWithEnrollment>;
                            return (
                                <tr
                                    data-index={virtualRow.index}
                                    ref={(node) =>
                                        rowVirtualizer.measureElement(node)
                                    }
                                    key={row.id}
                                    style={{
                                        position: "absolute",
                                        transform: `translateY(${virtualRow.start}px)`,
                                        width: "100%",
                                    }}
                                    className="border-b py-[0.4rem] border-gray-700 flex flex-row items-center justify-items-center"
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                style={{
                                                    width: cell.column.getSize(),
                                                }}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {enrollmentQuery.isFetching && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                        <Spinner size="lg" color="primary" label="Loading..." />
                    </div>
                )}
            </div>
            <Divider />
            <div className="flex justify-end bg-white gap-8 p-2">
                <Card>
                    <CardHeader className="text-lg font-semibold pb-0">
                        Enrolled Field
                    </CardHeader>
                    <Divider />
                    <CardBody>
                        <div className="grid grid-cols-3 divide-solid gap-x-3 items-end justify-items-end">
                            <div className="font-semibold text-right"></div>
                            <div className="font-semibold">Acres</div>
                            <div className="font-semibold">Value</div>
                            <div className="text-right">Basic</div>
                            <div>{summary.basicAcres.toLocaleString()}</div>
                            <div>{formatCurrency(summary.basicValue)}</div>
                            <div className="text-right">Advanced</div>
                            <div>{summary.advancedAcres.toLocaleString()}</div>
                            <div>{formatCurrency(summary.advancedValue)}</div>
                            <div className="text-right">Totals</div>
                            <div>{summary.enrolledAcres.toLocaleString()}</div>
                            <div>{formatCurrency(summary.enrolledValue)}</div>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader className="text-lg font-semibold pb-0">
                        Unenrolled Fields
                    </CardHeader>
                    <Divider />
                    <CardBody>
                        <div className="grid grid-cols-3 divide-solid gap-x-3 items-end justify-items-end">
                            <div className="font-semibold"></div>
                            <div className="font-semibold">
                                Active Field Acres
                            </div>
                            <div className="font-semibold">
                                Inactive Field Acres
                            </div>
                            <div className="col-span-3"></div>
                            <div className="col-span-3"></div>
                            <div>Totals</div>
                            <div>
                                {summary.activeUnenrolledAcres.toLocaleString()}
                            </div>
                            <div>
                                {summary.inactiveUnenrolledAcres.toLocaleString()}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
            {showEnrollmentForm && (
                <EnrollmentForm
                    fieldsToEnroll={fieldsToEnroll}
                    remainingBalance={
                        remainingBalance?.data?.remaining_retail_value ?? 0
                    }
                    existingEnrollment={
                        fieldsToEnroll[0].active_enrollment
                            ? fieldsToEnroll[0]
                            : undefined
                    }
                    onClose={(shouldRefetch) => {
                        if (shouldRefetch) {
                            enrollmentQuery.refetch();
                        }
                        setShowEnrollmentForm(false);
                    }}
                />
            )}
            {showRefundForm && (
                <RefundForm
                    enrollment={fieldsToEnroll[0]}
                    onClose={() => {
                        setShowRefundForm(false);
                        enrollmentQuery.refetch();
                    }}
                />
            )}
        </div>
    );
}
